(() => {
  const BUILDER_SELECTOR = '[data-essora-offer-builder]';
  const STEP_SELECTOR = '[data-essora-selection-step]';
  const CARD_SELECTOR = '.essora-offer-option';
  const SELECT_BUTTON_SELECTOR = '.essora-offer-option__select-button';

  const selectedCards = (step) => Array.from(step.querySelectorAll(`${CARD_SELECTOR}.is-selected`));

  const setCardSelected = (card, selected) => {
    const button = card.querySelector(SELECT_BUTTON_SELECTOR);
    if (!button) return;
    card.classList.toggle('is-selected', selected);
    button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    button.querySelector('span').textContent = selected ? button.dataset.selectedLabel : button.dataset.selectLabel;
  };

  const updateStepAvailability = (step) => {
    const required = Number(step.dataset.requiredCount || 1);
    const mode = step.dataset.selectionMode || 'single';
    const chosen = selectedCards(step);
    const isFull = mode === 'multiple' && chosen.length >= required;

    step.querySelectorAll(SELECT_BUTTON_SELECTOR).forEach((button) => {
      const isSelected = button.getAttribute('aria-pressed') === 'true';
      button.disabled = isFull && !isSelected;
      button.title = button.disabled ? 'Remove a selection before choosing another product.' : '';
    });
  };

  const updateBuilder = (builder) => {
    const steps = Array.from(builder.querySelectorAll(STEP_SELECTOR));
    let remaining = 0;
    let totalRequired = 1;
    let totalSelected = 1;

    steps.forEach((step) => {
      const required = Number(step.dataset.requiredCount || 1);
      const chosen = selectedCards(step).length;
      totalRequired += required;
      totalSelected += Math.min(chosen, required);
      remaining += Math.max(required - chosen, 0);
      updateStepAvailability(step);
    });

    const button = builder.querySelector('[data-essora-add-offer]');
    const status = builder.querySelector('[data-essora-selection-status]');
    if (!button || !status) return;

    button.disabled = remaining > 0;
    status.textContent = remaining > 0
      ? `Select ${remaining} more product${remaining === 1 ? '' : 's'} to complete this offer (${totalSelected}/${totalRequired}).`
      : `Your full offer is ready (${totalRequired}/${totalRequired}). Add everything to cart in one click.`;
  };

  const getHeroVariantId = (builder) => {
    const mainProductVariant = document.querySelector(
      'product-info product-form form[data-type="add-to-cart-form"] input[name="id"]:not(:disabled), section[id^="MainProduct-"] product-form form[data-type="add-to-cart-form"] input[name="id"]:not(:disabled)'
    );
    return Number(mainProductVariant?.value || builder.dataset.heroVariantId);
  };

  const getSelectedItems = (builder) => {
    const ids = [getHeroVariantId(builder)];

    builder.querySelectorAll(STEP_SELECTOR).forEach((step) => {
      selectedCards(step).forEach((card) => {
        const variantField = card.querySelector('.essora-offer-option__variant');
        ids.push(Number(variantField?.value));
      });
    });

    const quantities = new Map();
    ids.filter((id) => Number.isInteger(id) && id > 0).forEach((id) => {
      quantities.set(id, (quantities.get(id) || 0) + 1);
    });

    return Array.from(quantities, ([id, quantity]) => ({ id, quantity }));
  };

  const showNextIncompleteStep = (builder, currentStep) => {
    const steps = Array.from(builder.querySelectorAll(STEP_SELECTOR));
    const currentIndex = steps.indexOf(currentStep);
    const nextStep = steps.slice(currentIndex + 1).find((step) => {
      return selectedCards(step).length < Number(step.dataset.requiredCount || 1);
    });
    if (nextStep) nextStep.open = true;
  };

  const handleSelection = (builder, button) => {
    const step = button.closest(STEP_SELECTOR);
    const card = button.closest(CARD_SELECTOR);
    if (!step || !card) return;

    const mode = step.dataset.selectionMode || 'single';
    const isSelected = button.getAttribute('aria-pressed') === 'true';

    if (mode === 'single') {
      selectedCards(step).forEach((selectedCard) => setCardSelected(selectedCard, false));
      setCardSelected(card, true);
      step.open = false;
      showNextIncompleteStep(builder, step);
    } else {
      setCardSelected(card, !isSelected);
    }

    updateBuilder(builder);
  };

  const addOffer = async (builder, button) => {
    updateBuilder(builder);
    if (button.disabled) return;

    const error = builder.querySelector('[data-essora-builder-error]');
    const label = button.querySelector('[data-essora-submit-label]');
    const status = builder.querySelector('[data-essora-selection-status]');
    const items = getSelectedItems(builder);
    const requiredItemCount = 1 + Array.from(builder.querySelectorAll(STEP_SELECTOR))
      .reduce((sum, step) => sum + Number(step.dataset.requiredCount || 1), 0);
    const actualItemCount = items.reduce((sum, item) => sum + item.quantity, 0);

    if (actualItemCount !== requiredItemCount) {
      error.hidden = false;
      error.textContent = 'Please complete every offer step before adding it to cart.';
      updateBuilder(builder);
      return;
    }

    error.hidden = true;
    button.disabled = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    label.textContent = 'Adding Full Offer…';

    try {
      const payload = { items };

      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'The full offer could not be added.');
      }

      label.textContent = 'Full Offer Added';
      status.textContent = 'All selected products were added to your cart. Your eligible promotion will apply automatically.';

      window.location.assign(window.routes?.cart_url || '/cart');
    } catch (requestError) {
      error.hidden = false;
      error.textContent = requestError.message || 'Something went wrong. Please try again.';
      label.textContent = 'Add Full Offer to Cart';
      button.disabled = false;
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  };

  const initializeBuilder = (builder) => {
    if (builder.dataset.essoraBuilderReady === 'true') return;
    builder.dataset.essoraBuilderReady = 'true';

    builder.addEventListener('click', (event) => {
      const selectButton = event.target.closest(SELECT_BUTTON_SELECTOR);
      if (selectButton) {
        event.preventDefault();
        handleSelection(builder, selectButton);
        return;
      }

      const addButton = event.target.closest('[data-essora-add-offer]');
      if (addButton) {
        event.preventDefault();
        addOffer(builder, addButton);
      }
    });

    builder.addEventListener('change', (event) => {
      if (event.target.matches('.essora-offer-option__variant')) updateBuilder(builder);
    });

    updateBuilder(builder);
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll(BUILDER_SELECTOR).forEach(initializeBuilder);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
